import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/lubim-pech";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const lubimPech: CaseStudy = {
  slug: "lubim-pech",
  title: "Любим Печь",
  cardTitle: "Разработка и продвижение сайта доставки еды «Любим Печь»",
  subtitle: "Доставка еды из дровяной печи",
  industry: { label: "Еда и продукты", slug: "food" },
  eyebrow: "Интернет-магазин · Доставка еды",
  h1: "Разработка и продвижение сайта доставки «Любим Печь»",
  lead:
    "Разработали и продвигаем сайт доставки еды из дровяной печи «Любим Печь» в Краснодаре: меню по категориям, оформление заказа с доставкой, акции и отзывы.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт доставки" },

  seo: {
    primaryKeyword: "разработка и продвижение сайта доставки еды",
    secondaryKeywords: [
      "сайт доставки еды разработка",
      "интернет-магазин для ресторана",
      "сайт кафе с доставкой",
      "продвижение доставки еды",
    ],
    semanticEntities: [
      "меню по категориям",
      "хачапури",
      "пицца",
      "ламаджо",
      "женгялов хац",
      "пиде",
      "шашлык",
      "люля",
      "сеты",
      "десерты",
      "оформление заказа",
      "доставка и оплата",
      "акции",
      "отзывы",
    ],
    excludedKeywords: [
      "доставка еды Краснодар",
      "хачапури заказать",
      "шашлык доставка",
      "пицца заказать",
    ],
    title: "Сайт доставки «Любим Печь» — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт доставки еды из дровяной печи в Краснодаре с меню, категориями, заказом, доставкой, акциями и продвижением.",
    h1: "Разработка и продвижение сайта доставки «Любим Печь»",
    slug: "lubim-pech",
    canonicalUrl: `${SITE_URL}/cases/lubim-pech`,
    ogTitle: "Сайт доставки еды «Любим Печь»",
    ogDescription:
      "Меню по категориям, оформление заказа с доставкой, акции и отзывы для сервиса доставки еды из дровяной печи.",
    ogImage: `${SITE_URL}${dir}/lubim-pech-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Меню, заказ и доставка",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость меню и категорий блюд",
    },
    relatedCases: [
      {
        label: "Разработка интернет-магазина фермерских продуктов «Калачёво»",
        to: "/cases/kalachevo",
        note: "Каталог и доставка продуктов питания",
      },
      {
        label: "Разработка интернет-магазина цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, заказ и доставка",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт доставки еды?",
    text: "Спроектируем меню по категориям, оформление заказа с доставкой и подключим продвижение.",
    button: "Обсудить сайт доставки",
  },

  client: {
    name: "Любим Печь",
    url: "https://lubim-pech.ru/",
    city: "Краснодар",
    industry: "Доставка еды",
  },
  year: "2024",
  services: [
    "Дизайн сайта доставки",
    "Разработка меню по категориям",
    "Оформление заказа и доставка",
    "Раздел акций и отзывов",
    "SEO-продвижение",
  ],
  platform: "Собственная CMS",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
    { label: "SEO", to: routes.seo },
    { label: "Поддержка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "Любим Печь" },
    { label: "Тип проекта", value: "Сайт доставки еды" },
    { label: "Платформа", value: "Собственная CMS" },
    { label: "Год", value: "2024" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Меню, разделённое на ламаджо, хачапури, пиццу, женгялов хац, пиде, шашлык, люля, сеты, десерты и напитки",
      "Оформление заказа с именем, телефоном и вариантом доставки",
      "Раздел доставки и оплаты",
      "Раздел акций",
      "Раздел отзывов и контакты",
      "Продвижение сайта доставки в Краснодаре",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "«Любим Печь» — сервис доставки еды из дровяной печи в Краснодаре с большим меню: от хачапури и пиццы до шашлыка и десертов. Нужен был сайт, где покупатель быстро находит категорию и оформляет заказ с доставкой.",
        "Вторая часть задачи — продвижение: сайт должен собирать трафик по коммерческим запросам доставки еды и приводить новых покупателей.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тёплая подача блюд из печи",
    paragraphs: [
      "Интерфейс собран на тёплой terracotta/cream подложке — она перекликается с продуктом и не спорит с фотографиями блюд.",
      "Категории меню и карточки блюд выстроены так, чтобы покупатель быстро переходил от выбора блюда к оформлению заказа.",
    ],
    bullets: [
      "Тёплая terracotta/cream подложка вместо белого фона",
      "Категории меню с понятной навигацией",
      "Единый сценарий от выбора блюда до оформления заказа",
    ],
  },

  identity: {
    fonts: [
      { name: "Futura PT", role: "Заголовки", sample: "Меню Любим Печь" },
      { name: "Futura PT", role: "Интерфейс и описания", sample: "Оформление заказа" },
    ],
    colors: [
      { hex: "#F3E7DA", name: "Фон витрины" },
      { hex: "#B5622E", name: "Акцент бренда" },
      { hex: "#2A1E17", name: "Текст" },
    ],
  },

  cover: img(
    "lubim-pech-case-cover",
    "Главная страница сайта доставки еды «Любим Печь»",
    6,
  ),

  gallery: [
    img("lubim-pech-menu", "Меню по категориям на сайте доставки «Любим Печь»", 6),
    img("lubim-pech-order-delivery", "Оформление заказа и доставка на сайте «Любим Печь»", 3),
    img("lubim-pech-reviews-promo", "Раздел акций и отзывов на сайте «Любим Печь»", 3),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: выбор категории меню, карточка блюда и оформление заказа в один сценарий.",
      "Состояния заказа и доставки в мобильной версии показаны заполненными, как в реальном использовании.",
    ],
    images: [
      mobile("lubim-pech-mobile-hero", "Мобильная главная сайта доставки «Любим Печь»"),
      mobile("lubim-pech-mobile-menu", "Мобильное меню сайта доставки «Любим Печь»"),
    ],
  },

  columnBlocks: [
    {
      id: "menu",
      title: "Меню по категориям",
      paragraphs: [
        "Меню разделено на понятные категории — от ламаджо и хачапури до сетов и десертов. Покупатель быстро находит нужный раздел и переходит к заказу.",
      ],
      columns: [
        { title: "Основное", items: ["Ламаджо и хачапури", "Пицца и пиде"] },
        { title: "Мясо", items: ["Шашлык", "Люля"] },
        { title: "Дополнительно", items: ["Сеты", "Десерты и напитки"] },
      ],
    },
    {
      id: "order",
      title: "Заказ и доставка",
      paragraphs: [
        "Оформление заказа собрано в один шаг: имя, телефон и вариант доставки. Раздел доставки и оплаты объясняет условия без лишних переходов.",
      ],
      bullets: [
        "Оформление заказа с именем и телефоном",
        "Выбор варианта доставки",
        "Раздел условий доставки и оплаты",
      ],
    },
    {
      id: "promotion",
      title: "Акции, отзывы и продвижение",
      paragraphs: [
        "Раздел акций и отзывов поддерживает доверие к сервису, а SEO-продвижение приводит на сайт покупателей, которые ищут доставку еды из дровяной печи.",
      ],
      bullets: [
        "Раздел акций",
        "Раздел отзывов и контакты",
        "SEO-продвижение сайта доставки",
      ],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка интернет-магазина", to: routes.ecom },
      { label: "SEO-продвижение", to: routes.seo },
    ],
  },
};
